// Shared utility functions for IdeasReview components

export const IDEA_FLOW_STAGE_NAMES = [
  "Spark",
  "Shape",
  "Structure",
  "Scale",
  "Shine",
];

export const getIdeaFlowStageName = (indexOrStageNumber, { zeroBased = true } = {}) => {
  const index = zeroBased ? indexOrStageNumber : indexOrStageNumber - 1;
  if (index >= 0 && index < IDEA_FLOW_STAGE_NAMES.length) {
    return IDEA_FLOW_STAGE_NAMES[index];
  }
  return null;
};

export const getIdeaFlowStageTheme = (stepName) => {
  const themes = {
    Spark: {
      badge: "bg-violet-600 text-white shadow-md",
      bar: "bg-violet-500",
      ring: "group-hover:ring-violet-200",
      timeline: {
        gradient: "from-violet-500 to-violet-400",
        ring: "border-violet-300 bg-violet-50",
        ringActive: "border-violet-500 bg-violet-100 shadow-md shadow-violet-200/60 ring-4 ring-violet-100",
        dot: "bg-violet-600",
        label: "text-violet-700",
        labelMuted: "text-violet-400",
        connector: "bg-violet-400",
        icon: "lucide:flame",
      },
    },
    Shape: {
      badge: "bg-emerald-600 text-white shadow-md",
      bar: "bg-emerald-500",
      ring: "group-hover:ring-emerald-200",
      timeline: {
        gradient: "from-emerald-500 to-emerald-400",
        ring: "border-emerald-300 bg-emerald-50",
        ringActive: "border-emerald-500 bg-emerald-100 shadow-md shadow-emerald-200/60 ring-4 ring-emerald-100",
        dot: "bg-emerald-600",
        label: "text-emerald-700",
        labelMuted: "text-emerald-400",
        connector: "bg-emerald-400",
        icon: "lucide:pen-tool",
      },
    },
    Structure: {
      badge: "bg-orange-500 text-white shadow-md",
      bar: "bg-orange-500",
      ring: "group-hover:ring-orange-200",
      timeline: {
        gradient: "from-orange-500 to-amber-400",
        ring: "border-orange-300 bg-orange-50",
        ringActive: "border-orange-500 bg-orange-100 shadow-md shadow-orange-200/60 ring-4 ring-orange-100",
        dot: "bg-orange-500",
        label: "text-orange-700",
        labelMuted: "text-orange-400",
        connector: "bg-orange-400",
        icon: "lucide:blocks",
      },
    },
    Scale: {
      badge: "bg-blue-600 text-white shadow-md",
      bar: "bg-blue-500",
      ring: "group-hover:ring-blue-200",
      timeline: {
        gradient: "from-blue-500 to-blue-400",
        ring: "border-blue-300 bg-blue-50",
        ringActive: "border-blue-500 bg-blue-100 shadow-md shadow-blue-200/60 ring-4 ring-blue-100",
        dot: "bg-blue-600",
        label: "text-blue-700",
        labelMuted: "text-blue-400",
        connector: "bg-blue-400",
        icon: "lucide:trending-up",
      },
    },
    Shine: {
      badge: "bg-sky-500 text-white shadow-md",
      bar: "bg-sky-500",
      ring: "group-hover:ring-sky-200",
      timeline: {
        gradient: "from-sky-500 to-cyan-400",
        ring: "border-sky-300 bg-sky-50",
        ringActive: "border-sky-500 bg-sky-100 shadow-md shadow-sky-200/60 ring-4 ring-sky-100",
        dot: "bg-sky-500",
        label: "text-sky-700",
        labelMuted: "text-sky-400",
        connector: "bg-sky-400",
        icon: "lucide:sparkles",
      },
    },
  };
  return themes[stepName] || themes.Spark;
};

// Format date helper function
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

// Sort rank: ACTIVE first, REJECTED last, everything else in between
export const getIdeaStatusSortRank = (status) => {
  const normalized = (status || "").toUpperCase();
  if (normalized === "ACTIVE") return 0;
  if (normalized === "REJECTED") return 2;
  return 1;
};

export const sortIdeasByStatus = (ideas, { getStatus = (item) => item.status, getCreatedAt = (item) => item.createdAt } = {}) => {
  return [...ideas].sort((a, b) => {
    const rankA = getIdeaStatusSortRank(getStatus(a));
    const rankB = getIdeaStatusSortRank(getStatus(b));
    if (rankA !== rankB) return rankA - rankB;
    const dateA = getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0;
    const dateB = getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0;
    return dateB - dateA;
  });
};

export const getIdeaStatusBadgeClasses = (status) => {
  const normalized = (status || "").toUpperCase();
  const configs = {
    ACTIVE: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", dot: "bg-green-500" },
    REJECTED: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
    PENDING: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", dot: "bg-yellow-500" },
    INACTIVE: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", dot: "bg-slate-400" },
    DRAFT: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-500" },
  };
  return configs[normalized] || { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", dot: "bg-slate-500" };
};

export const formatIdeaStatusLabel = (status) => {
  if (!status) return "Unknown";
  return String(status).replace(/_/g, " ");
};

/** API current_step is 1-based; timeline activeStage is 0-based */
export const getMaxAccessibleStageIndex = (currentStep) =>
  Math.max(0, (Number(currentStep) || 1) - 1);

export const canAccessStage = (stageIndex, currentStep) =>
  stageIndex <= getMaxAccessibleStageIndex(currentStep);

export const getFrontierStageIndex = (currentStep) =>
  getMaxAccessibleStageIndex(currentStep);

/** Initial timeline tab from API current_step (1-based → 0-based index) */
export const getActiveStageIndexFromCurrentStep = (currentStep) =>
  getMaxAccessibleStageIndex(currentStep);

export const openFileUrl = (url) => {
  if (!url || typeof url !== "string") return;
  const trimmed = url.trim();
  if (!trimmed) return;
  window.open(trimmed, "_blank", "noopener,noreferrer");
};

/** Normalize student user object from idea-user / idea detail API */
export const mapStudentContact = (student) => {
  if (!student) return null;
  const name = `${student.first_name || ""} ${student.last_name || ""}`.trim();
  if (!name && !student.email) return null;
  return {
    id: student.id,
    name: name || student.email || "",
    email: student.email || "",
    avatar: student.profile_picture_url || "",
  };
};

/** Find primary student on varied API shapes */
export const resolveStudentFromRecord = (data) => {
  if (!data) return null;

  const fromStudent = (user) => mapStudentContact(user);

  const direct =
    fromStudent(data.primaryStudent) ||
    fromStudent(data.primary_student) ||
    fromStudent(data.user) ||
    fromStudent(data.student);
  if (direct) return direct;

  const ideaUsers = data.idea_users || data.ideaUsers;
  if (Array.isArray(ideaUsers)) {
    const primaryRow = ideaUsers.find(
      (row) =>
        row?.role === "PRIMARY_STUDENT" ||
        row?.role === "primary_student" ||
        row?.role === "student"
    );
    const primaryUser = primaryRow?.user ?? primaryRow;
    const fromPrimary = fromStudent(primaryUser);
    if (fromPrimary) return fromPrimary;

    const anyUserRow = ideaUsers.find((row) => row?.user);
    const fromAny = fromStudent(anyUserRow?.user);
    if (fromAny) return fromAny;
  }

  return null;
};

/** Normalize mentor user object from idea-user / idea detail API */
export const mapMentorContact = (mentor) => {
  if (!mentor) return null;
  const name = `${mentor.first_name || ""} ${mentor.last_name || ""}`.trim();
  if (!name && !mentor.email) return null;
  return {
    id: mentor.id,
    name: name || mentor.email || "Mentor",
    email: mentor.email || "",
    phone: mentor.phone || "",
    avatar: mentor.profile_picture_url || "",
  };
};

/** Map idea-user / mentor-idea API row to IdeaFlowCard props */
export const mapIdeaRecordToCardData = (item) => {
  const idea = item?.idea ?? item;
  if (!idea?.id) return null;

  const problemStatement = idea.problem_statement || "";
  const truncatedDescription =
    problemStatement.length > 100
      ? `${problemStatement.substring(0, 100)}...`
      : problemStatement;

  const studentRaw =
    item?.primaryStudent ??
    item?.user ??
    (item?.role === "PRIMARY_STUDENT" ? item : null);
  const student =
    studentRaw && (studentRaw.first_name != null || studentRaw.email != null)
      ? studentRaw
      : null;
  const studentName = student
    ? `${student.first_name || ""} ${student.last_name || ""}`.trim() ||
      student.email ||
      ""
    : "";

  const currentStep = idea.current_step ?? 1;
  const stepName = getIdeaFlowStageName(currentStep, { zeroBased: false }) || "Spark";

  const mentorContact = mapMentorContact(item?.mentor);
  const tags = [];
  if (studentName) {
    tags.push({
      id: `student-${student?.id ?? studentName}`,
      label: studentName,
      avatar: student?.profile_picture_url || null,
      icon: "lucide:user",
      role: "student",
    });
  }
  if (mentorContact?.name) {
    tags.push({
      id: `mentor-${mentorContact.id ?? mentorContact.name}`,
      label: mentorContact.name,
      avatar: mentorContact.avatar || null,
      icon: "lucide:graduation-cap",
      role: "mentor",
    });
  }
  if (idea.participation_mode) {
    tags.push({
      id: `mode-${idea.participation_mode}`,
      label: idea.participation_mode === "team" ? "Team" : "Solo",
      icon: idea.participation_mode === "team" ? "lucide:users" : "lucide:user",
    });
  }

  const categoryRaw = idea.innovation_category || "";
  const categoryLabel = categoryRaw
    ? categoryRaw.split(",")[0].trim()
    : "";

  return {
    id: idea.id,
    title: idea.title || "",
    description: truncatedDescription,
    image:
      idea.idea_image ||
      idea.concept_diagram ||
      idea.idea_video_link ||
      "https://via.placeholder.com/640x400?text=No+Image",
    submissionDate: formatDate(idea.created_at),
    status: idea.status || "",
    currentStep,
    stepName,
    category: categoryLabel,
    primaryImpact: idea.primary_impact || "",
    studentId: student?.id != null ? String(student.id) : null,
    primaryStudent: student,
    mentor: mentorContact,
    tags,
    _original: item,
  };
};

// Extract ideas data from API response
export const extractIdeasData = (response) => {
  let ideasData = [];
  if (response.success) {
    // Try response.data.data (most common structure)
    if (Array.isArray(response.data?.data)) {
      ideasData = response.data.data;
    } else if (Array.isArray(response.data)) {
      ideasData = response.data;
    } else if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
      ideasData = response.data.data.data;
    }
  }
  return ideasData;
};

